import React, { useEffect, useState } from "react";
import { useAuth } from "../AuthContext";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { GraduationCap, Mail, Lock, User as UserIcon, ArrowRight, Eye, EyeOff } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function Login() {
  const { user, setUser, API } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("signin"); // 'signin' | 'signup'
  const [form, setForm] = useState({ name: "", email: "", password: "", confirm_password: "", role: "learner" });
  const [busy, setBusy] = useState(false);
  const [showPw, setShowPw] = useState(false);

  useEffect(() => { if (user) navigate("/dashboard"); }, [user, navigate]);

  const set = (k, v) => setForm({ ...form, [k]: v });

  const signIn = async (e) => {
    e?.preventDefault();
    if (!form.email || !form.password) return toast.error("Enter email and password");
    setBusy(true);
    try {
      const r = await axios.post(`${API}/auth/login`,
        { email: form.email.trim(), password: form.password },
        { withCredentials: true });
      setUser(r.data.user);
      toast.success(`Welcome back, ${r.data.user.name}`);
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const signUp = async (e) => {
    e?.preventDefault();
    if (!form.name.trim()) return toast.error("Enter your name");
    if (!form.email.trim()) return toast.error("Enter your email");
    if (form.password.length < 8) return toast.error("Password must be at least 8 characters");
    if (form.password !== form.confirm_password) return toast.error("Passwords don't match");
    setBusy(true);
    try {
      const r = await axios.post(`${API}/auth/register`, {
        name: form.name.trim(), email: form.email.trim(),
        password: form.password, confirm_password: form.confirm_password, role: form.role,
      }, { withCredentials: true });
      setUser(r.data.user);
      toast.success(`Welcome, ${r.data.user.name}`);
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen hero-panel grain-bg">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-6 flex items-center justify-between relative z-10">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-md bg-[#0A231C] flex items-center justify-center">
            <GraduationCap size={18} className="text-white" />
          </div>
          <div className="font-heading text-xl font-semibold">SkillCrew</div>
        </div>
        <div className="overline hidden md:block">A calm place to learn deeply</div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-10 pt-6 pb-24 grid md:grid-cols-12 gap-10 relative z-10">
        <section className="md:col-span-7 flex flex-col justify-center">
          <div className="overline mb-4">Learning Management · Est. 2026</div>
          <h1 className="font-heading tracking-tight text-4xl sm:text-5xl lg:text-6xl font-semibold text-[#0A231C] leading-[1.05]">
            Teach a craft.<br/>
            Learn like it's a<br/>
            <span className="text-[#D45D3C]">quiet library</span>.
          </h1>
          <p className="mt-6 text-[#5C5C5C] text-lg max-w-xl">
            SkillCrew is a modern LMS for teachers who want to focus on the work, and learners who
            want to focus on their growth. Recorded courses, live webinars, honest progress.
          </p>

          <div className="mt-10 grid md:grid-cols-3 gap-4">
            <Feature k="For Learners" v="Category search, secure video playback, live webinars, honest progress." />
            <Feature k="For Teachers" v="Create courses, upload lessons, schedule webinars, submit for approval." />
            <Feature k="For IT Admins" v="Approve teachers & courses, set pricing, download source videos." />
          </div>
        </section>

        <aside className="md:col-span-5">
          <div className="card-surface p-8" data-testid="auth-card">
            <div className="flex bg-[#F1EFE7] rounded-lg p-1 mb-6" role="tablist">
              <button
                onClick={() => setMode("signin")}
                className={`flex-1 py-2 rounded-md text-sm font-medium transition-all ${mode === "signin" ? "bg-white shadow-sm text-[#0A231C]" : "text-[#5C5C5C]"}`}
                data-testid="tab-signin"
              >Sign in</button>
              <button
                onClick={() => setMode("signup")}
                className={`flex-1 py-2 rounded-md text-sm font-medium transition-all ${mode === "signup" ? "bg-white shadow-sm text-[#0A231C]" : "text-[#5C5C5C]"}`}
                data-testid="tab-signup"
              >Create account</button>
            </div>

            {mode === "signin" ? (
              <form onSubmit={signIn} data-testid="signin-form">
                <h2 className="font-heading text-2xl font-semibold text-[#0A231C]">Welcome back</h2>
                <p className="text-sm text-[#5C5C5C] mb-6">Sign in with your email and password.</p>
                <IconInput icon={<Mail size={16} />} label="Email" type="email" required
                           value={form.email} onChange={(v) => set("email", v)}
                           testid="signin-email-input" placeholder="you@example.com" />
                <IconInput icon={<Lock size={16} />} label="Password"
                           type={showPw ? "text" : "password"} required
                           value={form.password} onChange={(v) => set("password", v)}
                           testid="signin-password-input" placeholder="••••••••"
                           trailing={<button type="button" onClick={() => setShowPw(!showPw)} className="text-[#5C5C5C]" tabIndex={-1}>{showPw ? <EyeOff size={14} /> : <Eye size={14} />}</button>} />
                <button type="submit" disabled={busy}
                        className="btn-primary w-full mt-2 flex items-center justify-center gap-2"
                        data-testid="signin-submit-btn">
                  {busy ? "Signing in…" : (<>Sign in <ArrowRight size={16} /></>)}
                </button>
              </form>
            ) : (
              <form onSubmit={signUp} data-testid="signup-form">
                <h2 className="font-heading text-2xl font-semibold text-[#0A231C]">Create your account</h2>
                <p className="text-sm text-[#5C5C5C] mb-6">Pick your role, set a password, and you're in.</p>
                <IconInput icon={<UserIcon size={16} />} label="Full name" required
                           value={form.name} onChange={(v) => set("name", v)}
                           testid="signup-name-input" placeholder="Riya Sharma" />
                <IconInput icon={<Mail size={16} />} label="Email" type="email" required
                           value={form.email} onChange={(v) => set("email", v)}
                           testid="signup-email-input" placeholder="you@example.com" />
                <IconInput icon={<Lock size={16} />} label="Password"
                           type={showPw ? "text" : "password"} required
                           value={form.password} onChange={(v) => set("password", v)}
                           testid="signup-password-input" placeholder="At least 8 characters"
                           trailing={<button type="button" onClick={() => setShowPw(!showPw)} className="text-[#5C5C5C]" tabIndex={-1}>{showPw ? <EyeOff size={14} /> : <Eye size={14} />}</button>} />
                <IconInput icon={<Lock size={16} />} label="Confirm password"
                           type={showPw ? "text" : "password"} required
                           value={form.confirm_password} onChange={(v) => set("confirm_password", v)}
                           testid="signup-confirm-password-input" placeholder="Re-enter password" />

                <div className="mb-5">
                  <div className="overline mb-2">I want to sign up as</div>
                  <div className="grid grid-cols-2 gap-2" role="radiogroup">
                    <RoleOption id="learner" active={form.role === "learner"} onClick={() => set("role", "learner")} title="Learner" subtitle="Take courses & webinars" />
                    <RoleOption id="teacher" active={form.role === "teacher"} onClick={() => set("role", "teacher")} title="Teacher" subtitle="Create courses (needs approval)" />
                  </div>
                </div>

                <button type="submit" disabled={busy}
                        className="btn-primary w-full flex items-center justify-center gap-2"
                        data-testid="signup-submit-btn">
                  {busy ? "Creating account…" : (<>Create account <ArrowRight size={16} /></>)}
                </button>
              </form>
            )}

            <div className="flex items-center gap-3 my-5">
              <div className="flex-1 h-px bg-[#E5E3D8]" />
              <div className="overline">or</div>
              <div className="flex-1 h-px bg-[#E5E3D8]" />
            </div>

            <button
              onClick={googleLogin}
              className="w-full py-2.5 rounded-lg border border-[#E5E3D8] hover:border-[#0A231C] bg-white flex items-center justify-center gap-2 text-sm font-medium transition-colors"
              data-testid="google-signin-btn"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A10.98 10.98 0 0 0 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.06H2.18A10.99 10.99 0 0 0 1 12c0 1.78.43 3.46 1.18 4.94l3.66-2.84z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
              </svg>
              Continue with Google
            </button>
          </div>
        </aside>
      </div>
      <style>{`
        .input{width:100%;padding:0.7rem 0.9rem 0.7rem 2.5rem;border:1px solid #E5E3D8;border-radius:8px;background:white;outline:none;font-family:inherit;font-size:0.95rem;transition:border-color 150ms}
        .input:focus{border-color:#0A231C}
      `}</style>
    </div>
  );
}

function IconInput({ icon, label, type = "text", value, onChange, testid, placeholder, required, trailing }) {
  return (
    <label className="block mb-4">
      <div className="overline mb-1">{label}</div>
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5C5C]">{icon}</span>
        <input
          type={type}
          required={required}
          className="input"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          data-testid={testid}
        />
        {trailing && <span className="absolute right-3 top-1/2 -translate-y-1/2">{trailing}</span>}
      </div>
    </label>
  );
}

function RoleOption({ id, active, onClick, title, subtitle }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`role-option-${id}`}
      className={`p-3 rounded-lg border text-left transition-all ${active ? "border-[#0A231C] bg-[#0A231C] text-white" : "border-[#E5E3D8] hover:border-[#0A231C]"}`}
    >
      <div className={`font-heading font-semibold text-sm ${active ? "text-white" : "text-[#1C1C1C]"}`}>{title}</div>
      <div className={`text-xs mt-0.5 ${active ? "text-white/70" : "text-[#5C5C5C]"}`}>{subtitle}</div>
    </button>
  );
}

function Feature({ k, v }) {
  return (
    <div className="p-5 card-surface">
      <div className="overline mb-2">{k}</div>
      <div className="text-sm text-[#1C1C1C] leading-relaxed">{v}</div>
    </div>
  );
}
